import React, { useMemo } from 'react';
import { Stars } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'store';
import classnames from 'classnames';
import Draggable from 'react-draggable';

import ChatWindow from 'components/containers/ChatWindow';
import { useChatState } from 'hooks/useChatState';

import styles from 'theme/components/containers/ChatLauncher/ChatLauncher.module.scss';

const ChatLauncher: React.FC = () => {
  const { t } = useTranslation();
  const { chatFloatingWidth, chatFloatingHeight } = useSelector((state) => state.chat);
  
  const {
    isChatOpen,
    isChatDocked,
    isChatHorizontal,
    isChatResizable,
    isChatFullScreen,
    shouldShowAgentAI,
    chatWindowPosition,
    dragResetKey,
    closeChat,
    toggleChat,
    toggleDock,
    toggleHorizontal,
    toggleResizable,
    toggleFullScreen,
    handleDragStop
  } = useChatState({
    manageWindowPosition: true,
    enableKeyboardShortcuts: true
  });

  // Sprawdź czy okno chata powinno być renderowane
  const shouldRenderChatWindow = useMemo(
    () => isChatOpen && !isChatDocked && !isChatHorizontal,
    [isChatOpen, isChatDocked, isChatHorizontal]
  );

  // Klasy CSS dla przycisku launcher
  const buttonClassName = useMemo(
    () =>
      classnames(styles.chatLauncherButton, {
        [styles.pulsing]: !isChatOpen,
        [styles.chatLauncherButtonActive]: isChatOpen
      }),
    [isChatOpen]
  );

  // Klucz dla Draggable - wymusza remount przy zmianie trybu
  const draggableKey = useMemo(
    () =>
      `${isChatResizable ? 'popup-mode' : 'central-mode'}-${
        isChatFullScreen ? 'fullscreen' : 'normal'
      }-${dragResetKey}`,
    [isChatResizable, isChatFullScreen, dragResetKey]
  );

  // Ograniczenia dla przeciągania okna
  const draggableBounds = useMemo(() => {
    if (!isChatResizable || isChatFullScreen || !chatWindowPosition) {
      return undefined;
    }

    return {
      left: -chatWindowPosition.left,
      top: -chatWindowPosition.top,
      right: window.innerWidth - chatWindowPosition.left - chatFloatingWidth,
      bottom: window.innerHeight - chatWindowPosition.top - chatFloatingHeight
    };
  }, [isChatResizable, isChatFullScreen, chatWindowPosition, chatFloatingWidth, chatFloatingHeight]);

  // Pozycja dla Draggable
  const draggablePosition = useMemo(
    () => (isChatFullScreen ? { x: 0, y: 0 } : undefined),
    [isChatFullScreen]
  );

  // Klasy CSS dla wrappera okna chata
  const windowWrapperClassName = useMemo(
    () =>
      classnames(styles.chatWindowWrapper, {
        [styles.chatWindowWrapperOpen]: isChatOpen,
        [styles.chatWindowWrapperPositioned]:
          chatWindowPosition !== null && !isChatFullScreen && !isChatResizable,
        [styles.chatWindowWrapperFullScreen]: isChatFullScreen
      }),
    [isChatOpen, chatWindowPosition, isChatFullScreen, isChatResizable]
  );

  // Style dla wrappera okna chata
  const windowWrapperStyle = useMemo(
    () => ({
      left: isChatFullScreen ? 0 : chatWindowPosition?.left,
      top: isChatFullScreen ? 0 : chatWindowPosition?.top,
      width: isChatFullScreen ? undefined : `${chatFloatingWidth}px`
    }),
    [isChatFullScreen, chatWindowPosition, chatFloatingWidth]
  );

  // Nie renderuj, jeśli agent nie powinien być widoczny
  if (!shouldShowAgentAI()) return null;

  return (
    <div className={styles.chatLauncher}>
      {/* Przycisk do otwierania/zamykania chata */}
      <button
        className={buttonClassName}
        onClick={toggleChat}
        aria-label={t('Otwórz czat z agentem')}>
        {!isChatOpen && <Stars size={16} className={styles.chatLauncherButtonIcon} />}
        {isChatOpen ? t('Zamknij AI') : t('Zapytaj AI')}
      </button>

      {/* Okno chata w trybie floating/fullscreen */}
      {shouldRenderChatWindow && (
        <Draggable
          key={draggableKey}
          cancel="iframe, button, .navigationBar, .dockButton, .resizeHandleCorner"
          disabled={isChatDocked || isChatHorizontal || !isChatResizable || isChatFullScreen}
          handle=".dragHandle"
          defaultPosition={{ x: 0, y: 0 }}
          position={draggablePosition}
          bounds={draggableBounds}
          onStop={handleDragStop}>
          <div className={windowWrapperClassName} style={windowWrapperStyle}>
            <ChatWindow
              variant="floating"
              onCloseChat={closeChat}
              onToggleDock={toggleDock}
              onToggleHorizontal={toggleHorizontal}
              onToggleResizable={toggleResizable}
              onToggleFullScreen={toggleFullScreen}
              showNavigationBar
              showDragHandle={isChatResizable}
              isFullScreen={isChatFullScreen}
            />
          </div>
        </Draggable>
      )}
    </div>
  );
};

export default ChatLauncher;
